import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Three modes: 'light', 'dark', 'system'
  const [themeMode, setThemeMode] = useState(() => {
    const stored = localStorage.getItem('themeMode');
    return stored || 'system';
  });

  // Get the actual theme (resolved from 'system' if needed)
  const [resolvedTheme, setResolvedTheme] = useState('dark');

  useEffect(() => {
    const applyTheme = (mode) => {
      const root = document.documentElement;

      // Remove existing theme classes
      root.classList.remove('theme-light', 'theme-dark');

      if (mode === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const theme = prefersDark ? 'dark' : 'light';
        root.classList.add(`theme-${theme}`);
        setResolvedTheme(theme);
      } else {
        root.classList.add(`theme-${mode}`);
        setResolvedTheme(mode);
      }

      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        // Will be set based on CSS variables after theme is applied
        const isDark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
        metaThemeColor.setAttribute('content', isDark ? '#1c1917' : '#fafaf9');
      }
    };

    applyTheme(themeMode);
    localStorage.setItem('themeMode', themeMode);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themeMode === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const value = {
    themeMode,
    setThemeMode,
    resolvedTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
